'use client';

import * as React from 'react';

// UI
import { Typography } from '@components/typography';
import { IconTooltip } from '@components/icon-tooltip';
import { IconNames } from '@components/icon';
import { ReviewStars } from './review-stars';

type Props = {
  averageRating: number;
  reviewsCount: number;
  className?: string;
  mobileAlign?: 'start' | 'center';
};

// Helper function to get rating description
const getRatingDescription = (rating: number): string => {
  if (rating >= 4.8) return 'Outstanding';
  if (rating >= 4.5) return 'Excellent';
  if (rating >= 4.0) return 'Very Good';
  if (rating >= 3.5) return 'Good';
  if (rating >= 3.0) return 'Average';
  return 'Below Average';
};

export const ReviewStats: React.FC<Props> = ({
  averageRating,
  reviewsCount,
  className = '',
}) => {
  return (
    <div className={`flex justify-start ${className}`}>
      <div
        className={`inline-flex flex-col w-full sm:w-auto space-y-1 items-start text-left`}
      >
        <Typography
          variant="title1"
          component="span"
          className="text-3xl md:text-4xl font-medium text-primary-main w-full"
        >
          {averageRating.toFixed(2)}
        </Typography>

        <div className="flex items-center py-1 w-full">
          <ReviewStars rating={averageRating} isSummary={true} />
        </div>

        <Typography
          variant="title3"
          component="span"
          className="text-base md:text-md font-medium text-primary-main w-full"
        >
          {getRatingDescription(averageRating)}
        </Typography>

        <div className="flex items-center gap-1 w-full text-gray-main text-sm md:text-base">
          <Typography variant="body1" component="p">
            {reviewsCount == 1
              ? '1 Review'
              : reviewsCount > 1
                ? `${reviewsCount} Reviews`
                : ''}
          </Typography>
          {reviewsCount > 0 && (
            <IconTooltip
              icon={IconNames.QUESTION_CIRCLE}
              placement="top"
              size="sm"
              tooltipId="review-stats-tooltip"
            >
              Combined average and total number of ratings gathered from
              multiple platforms.
            </IconTooltip>
          )}
        </div>
      </div>
    </div>
  );
};
